/**
 * <video-highlights>
 * Instagram-style horizontal video carousel:
 * - all videos in view autoplay muted, pause when scrolled out of view
 * - per-card sound toggle (only one card plays audio at a time)
 * - tap/click a card to open it large in a lightbox, with sound + controls
 * - infinite loop in both directions, driven by a transform-based track.
 *   (No native scroll-snap / scrollIntoView: those fight each other in
 *   RTL layouts and cause visible "jump/refresh" glitches at the loop
 *   boundary, plus dead arrow buttons. A transform we fully control
 *   avoids both.)
 * - drag / swipe support via pointer events (content follows the finger)
 */

class VideoHighlights extends HTMLElement {
  connectedCallback() {
    if (this.__initialized) return;
    this.__initialized = true;

    this.viewport = this.querySelector('.video-highlights__viewport');
    this.track = this.querySelector('.video-highlights__track');
    if (!this.viewport || !this.track) return;

    const originalCards = Array.from(this.track.children);
    this.count = originalCards.length;
    if (this.count === 0) return;

    originalCards.forEach((card, i) => {
      card.setAttribute('data-slide-index', String(i));
      card.setAttribute('data-set', 'main');
    });

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (this.count > 1) {
      const beforeSet = originalCards.map((card) => this.#cloneCard(card, 'before'));
      const afterSet = originalCards.map((card) => this.#cloneCard(card, 'after'));

      const beforeFrag = document.createDocumentFragment();
      beforeSet.forEach((c) => beforeFrag.appendChild(c));
      this.track.insertBefore(beforeFrag, this.track.firstChild);

      const afterFrag = document.createDocumentFragment();
      afterSet.forEach((c) => afterFrag.appendChild(c));
      this.track.appendChild(afterFrag);
    }

    this.cards = Array.from(this.track.querySelectorAll('[data-video-card]'));
    // Start positioned on the first card of the "main" (real) set.
    this.pos = this.count > 1 ? this.count : 0;

    this.#setupSoundToggles();
    this.#setupAutoplay();
    this.#setupArrows();
    this.#setupDrag();
    this.#setupLightbox();

    window.addEventListener('resize', () => this.#applyTransform(false));

    // Position immediately (not via requestAnimationFrame) so the carousel
    // is correctly placed even if this runs in a backgrounded tab, where
    // rAF callbacks can be deferred indefinitely.
    this.#applyTransform(false);
  }

  #cloneCard(card, set) {
    const clone = card.cloneNode(true);
    clone.setAttribute('data-set', set);
    clone.setAttribute('aria-hidden', 'true');
    const video = clone.querySelector('video');
    if (video) {
      video.removeAttribute('id');
      video.muted = true;
    }
    return clone;
  }

  #cardStep() {
    const first = this.cards[0];
    if (!first) return 0;
    const rect = first.getBoundingClientRect();
    const styles = getComputedStyle(this.track);
    const gap = parseFloat(styles.columnGap || styles.gap) || 0;
    return rect.width + gap;
  }

  #applyTransform(animate) {
    const step = this.#cardStep();
    // NOTE: this container is RTL, so the flex track already lays out
    // index 0 at the leading (right) edge with each following index
    // further to the left. Shifting the track by +pos*step (not -pos*step)
    // is what brings card `pos` to the leading edge.
    if (!animate) {
      const prevTransition = this.track.style.transition;
      this.track.style.transition = 'none';
      this.track.style.transform = `translateX(${this.pos * step}px)`;
      // Force reflow so the "none" transition is committed before restoring.
      void this.track.offsetHeight;
      this.track.style.transition = prevTransition;
    } else {
      this.track.style.transform = `translateX(${this.pos * step}px)`;
    }
  }

  #goTo(pos, animate = true) {
    this.pos = pos;
    this.#applyTransform(animate);

    if (!animate || this.count <= 1) return;

    const onEnd = (event) => {
      if (event.target !== this.track || event.propertyName !== 'transform') return;
      this.track.removeEventListener('transitionend', onEnd);
      this.#normalizePosition();
    };
    this.track.addEventListener('transitionend', onEnd);
    // Fallback in case transitionend doesn't fire (e.g. a backgrounded tab).
    setTimeout(() => {
      this.track.removeEventListener('transitionend', onEnd);
      this.#normalizePosition();
    }, 600);
  }

  #normalizePosition() {
    if (this.count <= 1) return;
    // Safe window spans half a set into the "before" clones through half a
    // set into the "after" clones, so it never cuts through the main set
    // itself (that was the bug: too-tight bounds forced a wrap while still
    // inside the real cards, producing a visible "jump/refresh" glitch).
    // A single ± this.count shift is always enough since pos only ever
    // moves by about one card per interaction.
    const lower = this.count * 0.5;
    const upper = this.count * 2.5;
    if (this.pos > upper) {
      this.pos -= this.count;
      this.#applyTransform(false);
    } else if (this.pos < lower) {
      this.pos += this.count;
      this.#applyTransform(false);
    }
  }

  #setupArrows() {
    const prev = this.querySelector('[data-arrow="prev"]');
    const next = this.querySelector('[data-arrow="next"]');

    prev?.addEventListener('click', () => this.#goTo(Math.round(this.pos) - 1));
    next?.addEventListener('click', () => this.#goTo(Math.round(this.pos) + 1));
  }

  #setupDrag() {
    let dragging = false;
    let startX = 0;
    let startPos = 0;
    let moved = false;
    // Movement past this many pixels counts as an intentional drag rather
    // than incidental jitter from an ordinary mouse click (touch taps are
    // naturally steadier than mouse clicks, which is why this only bit
    // desktop users: a small, accidental mouse-move between down and up
    // was enough to swallow the click meant for a button underneath).
    const MOVE_THRESHOLD = 8;

    const step = () => this.#cardStep() || 1;

    const onPointerDown = (event) => {
      if (this.count <= 1) return;
      // Never hijack clicks aimed at interactive controls sitting on top
      // of the track (sound toggle, lightbox close) into a drag gesture.
      if (event.target.closest('[data-sound-toggle], [data-lightbox-close]')) return;
      dragging = true;
      moved = false;
      startX = event.clientX;
      startPos = this.pos;
      this.track.style.transition = 'none';
      this.viewport.setPointerCapture?.(event.pointerId);
    };

    const onPointerMove = (event) => {
      if (!dragging) return;
      const dx = event.clientX - startX;
      if (Math.abs(dx) > MOVE_THRESHOLD) moved = true;
      // Content follows the finger/cursor: dragging left moves the track
      // left (and reveals what's further along), dragging right moves it
      // right — independent of the RTL base-position formula above.
      this.pos = startPos + dx / step();
      this.track.style.transform = `translateX(${this.pos * step()}px)`;
    };

    const endDrag = () => {
      if (!dragging) return;
      dragging = false;
      this.track.style.transition = '';
      this.#goTo(Math.round(this.pos));
    };

    this.viewport.addEventListener('pointerdown', onPointerDown);
    this.viewport.addEventListener('pointermove', onPointerMove);
    this.viewport.addEventListener('pointerup', endDrag);
    this.viewport.addEventListener('pointercancel', endDrag);
    this.viewport.addEventListener('pointerleave', () => {
      if (dragging) endDrag();
    });

    // Swallow the click that would otherwise fire right after a genuine
    // drag (e.g. releasing on top of the sound-toggle button).
    this.track.addEventListener(
      'click',
      (event) => {
        if (moved) {
          event.stopPropagation();
          event.preventDefault();
          moved = false;
        }
      },
      true
    );
  }

  #setupAutoplay() {
    if (this.reducedMotion) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const video = entry.target.querySelector('video');
          if (!video) continue;
          if (entry.isIntersecting) {
            const playPromise = video.play();
            if (playPromise && typeof playPromise.catch === 'function') {
              playPromise.catch(() => {});
            }
          } else {
            video.pause();
          }
        }
      },
      { root: this.viewport, threshold: 0.5 }
    );

    this.cards.forEach((card) => observer.observe(card));
  }

  #setupSoundToggles() {
    this.track.addEventListener('click', (event) => {
      const button = event.target.closest('[data-sound-toggle]');
      if (!button) return;

      const card = button.closest('[data-video-card]');
      const video = card?.querySelector('video');
      if (!video) return;

      const willUnmute = video.muted;

      // Mute every other video (across all cloned sets) so only one plays audio.
      this.track.querySelectorAll('video').forEach((v) => {
        v.muted = true;
      });
      this.track.querySelectorAll('[data-sound-toggle]').forEach((btn) => {
        btn.setAttribute('aria-pressed', 'false');
      });

      if (willUnmute) {
        video.muted = false;
        button.setAttribute('aria-pressed', 'true');
      }

      // Keep clones of the same slide in sync visually.
      const index = card.getAttribute('data-slide-index');
      if (index !== null) {
        this.track.querySelectorAll(`[data-slide-index="${index}"] [data-sound-toggle]`).forEach((btn) => {
          btn.setAttribute('aria-pressed', String(!video.muted));
        });
      }
    });
  }

  #setupLightbox() {
    this.lightbox = this.querySelector('[data-lightbox]');
    if (!this.lightbox) return;
    this.lightboxVideo = this.lightbox.querySelector('[data-lightbox-video]');
    const closeBtn = this.lightbox.querySelector('[data-lightbox-close]');

    // Open on any click on a card that isn't the sound toggle. Clicks that
    // followed a real drag are already stopped upstream in #setupDrag.
    this.track.addEventListener('click', (event) => {
      if (event.target.closest('[data-sound-toggle]')) return;
      const card = event.target.closest('[data-video-card]');
      if (!card) return;
      this.#openLightbox(card);
    });

    closeBtn?.addEventListener('click', () => this.#closeLightbox());
    this.lightbox.addEventListener('click', (event) => {
      if (event.target === this.lightbox) this.#closeLightbox();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !this.lightbox.hidden) this.#closeLightbox();
    });

    // Safety net: some iOS versions can still push a playsinline video into
    // native fullscreen on their own. If that happens, back out of it right
    // away so the X button stays the only way to "get bigger".
    this.lightboxVideo.addEventListener('webkitbeginfullscreen', () => {
      this.lightboxVideo.webkitExitFullscreen?.();
    });
  }

  #openLightbox(card) {
    const sourceVideo = card.querySelector('video');
    if (!sourceVideo || !this.lightbox || !this.lightboxVideo) return;

    // Remember which cards were actively playing so we can resume them
    // (autoplay won't re-trigger on its own since nothing scrolled).
    this.__resumeVideos = Array.from(this.track.querySelectorAll('video')).filter((v) => !v.paused);
    this.track.querySelectorAll('video').forEach((v) => v.pause());

    this.lightboxVideo.innerHTML = sourceVideo.innerHTML;
    if (sourceVideo.poster) this.lightboxVideo.poster = sourceVideo.poster;
    this.lightboxVideo.muted = false;
    // No native controls: the browser's own control bar adds its own
    // "expand to fullscreen" affordance on top of ours, which is exactly
    // the confusing double-enlarge behavior we're removing. The lightbox
    // already has one job (play the video large) and one exit (the X).
    this.lightboxVideo.controls = false;
    this.lightboxVideo.disablePictureInPicture = true;
    this.lightboxVideo.setAttribute('playsinline', '');
    this.lightboxVideo.setAttribute('webkit-playsinline', '');
    this.lightboxVideo.load();

    this.lightbox.hidden = false;
    document.body.style.overflow = 'hidden';

    const playPromise = this.lightboxVideo.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {
        // Autoplay-with-sound can be blocked in rare cases; fall back to muted.
        this.lightboxVideo.muted = true;
        this.lightboxVideo.play()?.catch(() => {});
      });
    }
  }

  #closeLightbox() {
    if (!this.lightbox || this.lightbox.hidden) return;
    this.lightboxVideo.pause();
    this.lightboxVideo.removeAttribute('src');
    this.lightboxVideo.innerHTML = '';
    this.lightbox.hidden = true;
    document.body.style.overflow = '';

    (this.__resumeVideos || []).forEach((v) => {
      const playPromise = v.play();
      if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(() => {});
    });
    this.__resumeVideos = [];
  }
}

if (!customElements.get('video-highlights')) {
  customElements.define('video-highlights', VideoHighlights);
}
